// src/pages/KycCapturePage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import './KycCapture.css';

const MIN_REVIEW = 0.4; // needs retake / manual review tier
const MIN_PASS = 0.65;  // looks OK

const confidenceToMeta = (c) => {
  if (typeof c !== 'number' || Number.isNaN(c)) return null;

  const pct = Math.max(0, Math.min(100, Math.round(c * 100)));

  if (c >= MIN_PASS) return { pct, tone: 'ok', title: 'ID looks valid' };
  if (c >= MIN_REVIEW) return { pct, tone: 'warn', title: 'ID photo is unclear' };
  return { pct, tone: 'bad', title: 'This does not look like an ID document' };
};

const humanizeWarnings = (warnings) => {
  const list = Array.isArray(warnings) ? warnings : [];
  const out = [];

  for (const w of list) {
    const s = String(w || '');

    if (/does not strongly match identity-document patterns/i.test(s)) {
      out.push('This file does not appear to be a passport, national ID, or driver’s license.');
      continue;
    }
    if (/no ocr text extracted/i.test(s)) {
      out.push('We could not read any text. Retake the photo with better focus and lighting.');
      continue;
    }
    if (/low ocr confidence/i.test(s)) {
      out.push('The photo is too blurry/dark. Retake with bright lighting and tap-to-focus on the text.');
      continue;
    }
    if (/scanned pdf ocr performed/i.test(s)) {
      out.push('We tried OCR on a scanned PDF. If the result is unclear, upload a clearer photo.');
      continue;
    }
    if (/pdf appears to be scanned/i.test(s)) {
      out.push('This PDF looks like a scan. Upload a clearer photo or a text-based PDF.');
      continue;
    }

    out.push(s);
  }

  if (out.length === 0 && list.length > 0) {
    out.push('Please retake the photo: full document visible, sharp focus, no glare, good lighting.');
  }

  return out;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const KycCapturePage = () => {
  const { sessionId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';

  const [idFile, setIdFile] = useState(null);
  const [addrFile, setAddrFile] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [idUploaded, setIdUploaded] = useState(false);
  const [addrUploaded, setAddrUploaded] = useState(false);

  const [idPreviewUrl, setIdPreviewUrl] = useState('');
  const [addrPreviewUrl, setAddrPreviewUrl] = useState('');

  const [idRejectMessage, setIdRejectMessage] = useState('');
  const [idConfidence, setIdConfidence] = useState(null);
  const [idWarnings, setIdWarnings] = useState([]);

  const [sessionValid, setSessionValid] = useState(true);

  // ✅ Once both docs uploaded, lock UI and show message.
  const [completed, setCompleted] = useState(false);

  // ✅ Used to remount <input type="file"> so selecting same file triggers onChange
  const [inputKey, setInputKey] = useState(0);

  const idPrevRef = useRef('');
  const addrPrevRef = useRef('');

  // -------------------- PREVIEWS --------------------
  useEffect(() => {
    if (idPrevRef.current) URL.revokeObjectURL(idPrevRef.current);

    if (idFile && String(idFile.type || '').toLowerCase().startsWith('image/')) {
      const url = URL.createObjectURL(idFile);
      idPrevRef.current = url;
      setIdPreviewUrl(url);
    } else {
      idPrevRef.current = '';
      setIdPreviewUrl('');
    }

    // Reset ID feedback when user changes ID file
    setIdRejectMessage('');
    setIdConfidence(null);
    setIdWarnings([]);
    setIdUploaded(false);
  }, [idFile]);

  useEffect(() => {
    if (addrPrevRef.current) URL.revokeObjectURL(addrPrevRef.current);

    if (addrFile && String(addrFile.type || '').toLowerCase().startsWith('image/')) {
      const url = URL.createObjectURL(addrFile);
      addrPrevRef.current = url;
      setAddrPreviewUrl(url);
    } else {
      addrPrevRef.current = '';
      setAddrPreviewUrl('');
    }

    setAddrUploaded(false);
  }, [addrFile]);

  useEffect(() => {
    return () => {
      if (idPrevRef.current) URL.revokeObjectURL(idPrevRef.current);
      if (addrPrevRef.current) URL.revokeObjectURL(addrPrevRef.current);
    };
  }, []);

  // -------------------- SESSION VALIDATION --------------------
  useEffect(() => {
    const validateSession = async () => {
      try {
        const response = await axiosInstance.get(`/kyc/capture-sessions/${sessionId}/validate`, {
          params: { token },
          skipAuthHandling: true,
          validateStatus: (s) => s < 500,
        });

        const valid = !!response?.data?.valid;
        setSessionValid(valid);

        if (!valid) {
          toast.error('Session expired or invalid. Please regenerate a QR code on your computer.');
        }
      } catch (error) {
        console.error('Session validation failed:', error);
        setSessionValid(false);
        toast.error('Could not validate session. Please regenerate a QR code.');
      }
    };

    if (sessionId && token) validateSession();
  }, [sessionId, token]);

  // ✅ When completed, remove token from URL for safety.
  useEffect(() => {
    if (!completed) return;

    try {
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch {
      // ignore
    }
  }, [completed]);

  // -------------------- VALIDATION --------------------
  const validateFileType = (file) => {
    if (!file) return 'Missing file';

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/pdf',
      'image/heic',
      'image/heif',
    ];

    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();

    const extOk =
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.png') ||
      name.endsWith('.pdf') ||
      name.endsWith('.heic') ||
      name.endsWith('.heif');

    if (!(allowedTypes.includes(type) || extOk)) {
      return 'File type not supported. Please upload JPG, PNG, HEIC/HEIF, or PDF.';
    }
    return null;
  };

  const validate = (file) => {
    const typeErr = validateFileType(file);
    if (typeErr) return typeErr;

    const maxSize = 30 * 1024 * 1024; // 30MB per phone file
    if (file.size > maxSize) return 'File too large. Please take a smaller/clear photo.';
    return null;
  };

  const canUpload = useMemo(() => {
    return (
      !!sessionId &&
      !!token &&
      sessionValid &&
      !!idFile &&
      !!addrFile &&
      !uploading &&
      !completed
    );
  }, [sessionId, token, sessionValid, idFile, addrFile, uploading, completed]);

  // -------------------- NOTIFY DESKTOP (NEW) --------------------
   const notifyDesktop = async () => {
  try {
    const notifyUrl = `/kyc/capture-sessions/${sessionId}/notify?token=${encodeURIComponent(token)}`;

    const res = await axiosInstance.get(notifyUrl, {
      skipAuthHandling: true,
      validateStatus: (s) => s < 500,
    });

    if (res.status === 204 || res.status === 200) return true;

    //console.log('Notify failed:', res.status, res.data);
    return false;
  } catch (e) {
    //console.log('Notify exception:', e);
    return false;
  }
};



const notifyDesktopWithRetry = async () => {
  const delays = [0, 500, 1000, 2000]; // Added one more retry
  for (let i = 0; i < delays.length; i++) {
    if (delays[i]) await sleep(delays[i]);
    try {
      //console.log(`Notify attempt ${i + 1} for session ${sessionId}`);
      const ok = await notifyDesktop();
      if (ok) {
        //console.log('Notify succeeded on attempt', i + 1);
        return true;
      }
    } catch (e) {
      //console.log('Notify attempt failed:', i + 1, e.message);
    }
  }
  return false;
};

  // -------------------- UPLOAD --------------------
  const upload = async () => {
    if (!canUpload) return;

    const idErr = validate(idFile);
    const addrErr = validate(addrFile);
    if (idErr || addrErr) {
      toast.error(idErr || addrErr);
      return;
    }

    setUploading(true);
    setProgress(0);
    setIdUploaded(false);
    setAddrUploaded(false);
    setIdRejectMessage('');
    setIdConfidence(null);
    setIdWarnings([]);

    try {
      const fd = new FormData();

      // ✅ Match .NET binder property names (case-insensitive, but safest)
      fd.append('IdDocument', idFile);
      fd.append('AddressProof', addrFile);

      const url = `/kyc/capture-sessions/${sessionId}/upload?token=${encodeURIComponent(token)}`;

      const res = await axiosInstance.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        skipAuthHandling: true,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          setProgress(pct);
        },
        validateStatus: (s) => s < 500, // let 400/422 flow through
      });

      if (res.status === 200) {
        setIdUploaded(true);
        setAddrUploaded(true);
        setCompleted(true);

        toast.success('Uploaded. Return to your computer — documents will appear shortly.');

        // ✅ NEW: notify desktop to refresh/import immediately
        const notified = await notifyDesktopWithRetry();
        if (!notified) {
          // Keep this non-fatal; desktop may still poll the status endpoint.
          //console.log('Desktop notify did not succeed (polling may still import).');
        }

        // Optional: clear local selection to reduce memory usage
        setIdFile(null);
        setAddrFile(null);
        setInputKey((k) => k + 1);

        return;
      }

      if (res.status === 400) {
        const msg = res.data?.message || 'Upload rejected. Please retake photos and try again.';
        const conf = typeof res.data?.confidence === 'number' ? res.data.confidence : null;
        const warns = Array.isArray(res.data?.warnings) ? res.data.warnings : [];

        setIdRejectMessage(msg);
        setIdConfidence(conf);
        setIdWarnings(warns);

        toast.error(msg);

        if (conf !== null && conf < MIN_REVIEW) {
          toast.info('Tip: upload a real ID (passport/national ID/driver’s license), not a logo or screenshot.');
        } else {
          toast.info('Tip: ensure the whole ID is visible, sharp focus, no glare, good lighting.');
        }
        return;
      }

      if (res.status === 422) {
        toast.error(res.data?.message || 'Upload rejected. Please retake photos and try again.');
        return;
      }

      if (res.status === 404) {
        toast.error(res.data?.message || 'Session not found or expired.');
        setSessionValid(false);
        return;
      }

      toast.error(res.data?.message || 'Upload failed.');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  // -------------------- UI --------------------
  if (!sessionId || !token) {
    return (
      <div className="kyc-capture-container">
        <div className="kyc-capture-card">
          <h2>Invalid or Expired Link</h2>
          <p>This capture link is invalid or expired. Please regenerate a QR code on your computer.</p>
        </div>
      </div>
    );
  }

  const renderStatus = (selected, uploaded) => {
    if (uploaded) return <span className="cap-pill cap-pill-ok">Uploaded</span>;
    if (selected) return <span className="cap-pill cap-pill-warn">Selected</span>;
    return <span className="cap-pill">Waiting</span>;
  };

  const confMeta = confidenceToMeta(idConfidence);
  const friendlyWarnings = humanizeWarnings(idWarnings);

  return (
    <div className="kyc-capture-container">
      <div className="kyc-capture-card">
        <h2>Capture Documents</h2>

        <p className="small">Use good lighting, avoid glare, and make sure the full document is visible.</p>

        {!sessionValid && (
          <div className="cap-error" style={{ marginTop: 10 }}>
            Session expired or invalid. Please regenerate a QR code on your computer.
          </div>
        )}

        {completed && (
          <div className="cap-success" style={{ marginTop: 10 }}>
            Upload complete. Return to your computer — the documents will appear automatically.
          </div>
        )}

        <div className="cap-group">
          <div className="cap-row">
            <label className="cap-label">1) Government-Issued ID</label>
            {renderStatus(!!idFile, idUploaded)}
          </div>

          <input
            key={`id-${inputKey}`}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={(e) => setIdFile(e.target.files?.[0] || null)}
            disabled={uploading || !sessionValid || completed}
          />

          {idFile && (
            <div className="small" style={{ marginTop: 6 }}>
              Selected: {idFile.name} ({(idFile.size / (1024 * 1024)).toFixed(2)} MB)
            </div>
          )}

          {idPreviewUrl && <img src={idPreviewUrl} alt="ID Preview" className="cap-preview" />}

          {(idRejectMessage || idConfidence !== null || (idWarnings && idWarnings.length > 0)) && (
            <div
              className={`cap-analysis ${confMeta?.tone ? `cap-analysis-${confMeta.tone}` : ''}`}
              style={{ marginTop: 10 }}
            >
              <div className="small" style={{ fontWeight: 700 }}>
                {confMeta?.title || 'ID check'}
              </div>

              {idRejectMessage && (
                <div className="small" style={{ marginTop: 4 }}>
                  {idRejectMessage}
                </div>
              )}

              {confMeta && (
                <div className="small" style={{ marginTop: 6 }}>
                  Recognition score: <strong>{confMeta.pct}%</strong>
                  {confMeta.pct < 65 && <span> (please retake / upload a real ID)</span>}
                </div>
              )}

              {friendlyWarnings.length > 0 && (
                <ul className="small" style={{ marginTop: 8, paddingLeft: 18 }}>
                  {friendlyWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}

              <div className="small" style={{ marginTop: 8 }}>
                Accepted: <strong>Passport</strong>, <strong>National ID</strong>, or{' '}
                <strong>Driver’s License</strong>. Do not upload logos, screenshots, or unrelated photos.
              </div>
            </div>
          )}
        </div>

        <div className="cap-group">
          <div className="cap-row">
            <label className="cap-label">2) Proof of Address</label>
            {renderStatus(!!addrFile, addrUploaded)}
          </div>

          <input
            key={`addr-${inputKey}`}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={(e) => setAddrFile(e.target.files?.[0] || null)}
            disabled={uploading || !sessionValid || completed}
          />

          {addrFile && (
            <div className="small" style={{ marginTop: 6 }}>
              Selected: {addrFile.name} ({(addrFile.size / (1024 * 1024)).toFixed(2)} MB)
            </div>
          )}

          {addrPreviewUrl && <img src={addrPreviewUrl} alt="Address Preview" className="cap-preview" />}
        </div>

        {uploading && (
          <div className="upload-progress" style={{ marginTop: 10 }}>
            <div className="progress-bar" style={{ width: `${progress}%` }}>
              {progress}%
            </div>
          </div>
        )}

        <button className="cap-button" onClick={upload} disabled={!canUpload}>
          {uploading ? 'Uploading...' : completed ? 'Uploaded' : 'Upload Photos'}
        </button>

        <div className="cap-tip">Tip: Tap-to-focus, avoid shadows, and retake if text is not crisp.</div>
      </div>
    </div>
  );
};

export default KycCapturePage;
